import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Avatar,
  LinearProgress,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AutoAwesome,
  TrendingUp,
  TrendingDown,
  Security,
  Psychology,
  Analytics,
  MonetizationOn,
  LocationOn,
  Home,
  Assessment,
  ExpandMore,
  Refresh,
  Download,
  Share,
  Warning,
  CheckCircle,
  Star,
  Timeline,
  PieChart,
  BarChart,
  ShowChart
} from '@mui/icons-material';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, AreaChart, Area } from 'recharts';
import { styled } from '@mui/material/styles';
import aiAnalyticsService from '../../../services/aiAnalyticsService';
import apiClient from '../../../services/apiClient';

const GradientCard = styled(Card)(({ theme, gradient }) => ({
  background: gradient,
  color: 'white',
  '& .MuiCardContent-root': {
    padding: theme.spacing(3),
  },
}));

const MetricCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const AIInsights = () => {
  const [properties, setProperties] = useState([]);
  const [insights, setInsights] = useState({
    marketInsights: null,
    riskAssessment: null,
    recommendations: null,
    portfolioOptimization: null,
    marketPrediction: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await apiClient.get('/listings', { params: { limit: 10000 } });
      const properties = response.data.data || response.data; // Handle both response structures
      setProperties(properties);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setError('Failed to load properties data');
    }
  };

  const generateAllInsights = async () => {
    if (!properties.length) return;

    setLoading(true);
    setError(null);

    try {
      const [marketInsights, riskAssessment, recommendations, portfolioOptimization, marketPrediction] = await Promise.all([
        aiAnalyticsService.getMarketInsights(properties),
        aiAnalyticsService.getRiskAssessment(properties),
        aiAnalyticsService.getPropertyRecommendations({ budget: 500000, riskTolerance: 'medium' }, properties),
        aiAnalyticsService.getPortfolioOptimization([], properties),
        aiAnalyticsService.getMarketTrendPrediction(generateMockHistoricalData())
      ]);

      setInsights({
        marketInsights,
        riskAssessment,
        recommendations,
        portfolioOptimization,
        marketPrediction
      });
    } catch (error) {
      console.error('Error generating insights:', error);
      setError('Failed to generate AI insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateMockHistoricalData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((month, index) => ({
      month,
      avgPrice: Math.floor(Math.random() * 100000) + 200000 + (index * 5000),
      volume: Math.floor(Math.random() * 50) + 20,
      index: index
    }));
  };

  const renderMarketInsights = () => {
    if (!insights.marketInsights) return null;

    const { marketSentiment, avgPriceTrend, topOpportunities, risks, hotspots, summary } = insights.marketInsights;

    return (
      <Grid item xs={12}>
        <Paper 
          elevation={1}
          sx={{ 
            p: 3, 
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Psychology sx={{ mr: 2, color: '#1976d2' }} />
              <Typography variant="h6" fontWeight="600" color="text.primary">
                AI Market Intelligence
              </Typography>
            </Box>
            <Chip 
              icon={<AutoAwesome />} 
              label="AI Powered" 
              size="small"
              sx={{ bgcolor: '#f5f5f5', color: '#1976d2', border: '1px solid #e0e0e0' }}
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h4" fontWeight="600" color="#1976d2" sx={{ mb: 1 }}>
                  {marketSentiment}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Market Sentiment
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h4" fontWeight="600" color="#ed6c02" sx={{ mb: 1 }}>
                  {avgPriceTrend}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Price Trend
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h4" fontWeight="600" color="#2e7d32" sx={{ mb: 1 }}>
                  {hotspots?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Market Hotspots
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Typography variant="body1" color="text.primary">
              {summary}
            </Typography>
          </Box>
        </Paper>
      </Grid>
    );
  };

  const renderRiskAssessment = () => {
    if (!insights.riskAssessment) return null;

    const { portfolioRiskScore, riskBreakdown, mitigationStrategies } = insights.riskAssessment;

    return (
      <Grid item xs={12} md={6}>
        <Paper 
          elevation={1}
          sx={{ 
            p: 3, 
            height: 400,
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Security sx={{ mr: 2, color: '#d32f2f' }} />
            <Typography variant="h6" fontWeight="600" color="text.primary">
              Risk Assessment
            </Typography>
          </Box>

          <Box sx={{ mb: 3, textAlign: 'center', p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Typography variant="h4" color="#d32f2f" fontWeight="600" gutterBottom>
              {portfolioRiskScore}/100
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overall Risk Score
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={portfolioRiskScore} 
              sx={{ mt: 1, height: 6, borderRadius: 3 }}
              color={portfolioRiskScore > 70 ? 'error' : portfolioRiskScore > 40 ? 'warning' : 'success'}
            />
          </Box>

          <Typography variant="subtitle1" fontWeight="600" gutterBottom>Risk Breakdown</Typography>
          <List dense>
            {Object.entries(riskBreakdown).map(([key, value]) => (
              <ListItem key={key} sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon>
                  <Assessment color={value > 60 ? 'error' : value > 40 ? 'warning' : 'success'} />
                </ListItemIcon>
                <ListItemText 
                  primary={key.charAt(0).toUpperCase() + key.slice(1)} 
                  secondary={`${value}% risk level`}
                />
              </ListItem>
            ))}
          </List>

          {mitigationStrategies && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>Mitigation Strategies</Typography>
              <List dense>
                {mitigationStrategies.slice(0, 2).map((strategy, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText primary={strategy} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      </Grid>
    );
  };

  const renderRecommendations = () => {
    if (!insights.recommendations?.recommendations) return null;

    return (
      <Grid item xs={12} md={6}>
        <Paper 
          elevation={1}
          sx={{ 
            p: 3, 
            height: 400,
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Star sx={{ mr: 2, color: '#ed6c02' }} />
            <Typography variant="h6" fontWeight="600" color="text.primary">
              Top Property Recommendations
            </Typography>
          </Box>

          <List>
            {insights.recommendations.recommendations.slice(0, 3).map((rec, index) => (
              <ListItem key={index} sx={{ px: 0, py: 1.5, borderBottom: index < 2 ? '1px solid #e0e0e0' : 'none' }}>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: '#f5f5f5', color: '#1976d2', width: 32, height: 32 }}>
                    {rec.score}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight="500">
                        Property #{rec.propertyId}
                      </Typography>
                      <Chip 
                        label={rec.riskLevel} 
                        size="small" 
                        sx={{
                          bgcolor: rec.riskLevel === 'Low' ? '#e8f5e8' : rec.riskLevel === 'Medium' ? '#fff3e0' : '#ffebee',
                          color: rec.riskLevel === 'Low' ? '#2e7d32' : rec.riskLevel === 'Medium' ? '#ed6c02' : '#d32f2f',
                          border: '1px solid #e0e0e0'
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        ROI Estimate: {rec.roiEstimate}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {rec.reasons?.slice(0, 2).join(', ')}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Grid>
    );
  };

  const renderMarketPrediction = () => {
    if (!insights.marketPrediction) return null;

    const { shortTermTrend, longTermTrend, priceChangePrediction, keyFactors, investmentTiming } = insights.marketPrediction;

    return (
      <Grid item xs={12}>
        <Paper 
          elevation={1}
          sx={{ 
            p: 3, 
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Timeline sx={{ mr: 2, color: '#9c27b0' }} />
            <Typography variant="h6" fontWeight="600" color="text.primary">
              Market Trend Prediction
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h5" fontWeight="600" color="#1976d2">
                  {shortTermTrend}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  6-Month Outlook
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h5" fontWeight="600" color="#ed6c02">
                  {longTermTrend}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  12-Month Outlook
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h5" fontWeight="600" color="#2e7d32">
                  {priceChangePrediction}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Price Change
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h5" fontWeight="600" color="#9c27b0">
                  {investmentTiming}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Investment Timing
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {keyFactors && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>Key Market Factors</Typography>
              <Grid container spacing={1}>
                {keyFactors.map((factor, index) => (
                  <Grid item key={index}>
                    <Chip 
                      label={factor} 
                      size="small"
                      sx={{
                        bgcolor: '#f5f5f5',
                        color: 'text.primary',
                        border: '1px solid #e0e0e0'
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>
      </Grid>
    );
  };

  const renderPortfolioOptimization = () => {
    if (!insights.portfolioOptimization) return null;

    const { expectedROI, riskReduction, actionPlan, allocationStrategy } = insights.portfolioOptimization;

    return (
      <Grid item xs={12}>
        <MetricCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PieChart sx={{ mr: 2, color: 'success.main', fontSize: 32 }} />
              <Typography variant="h6" fontWeight="bold">
                Portfolio Optimization
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 2, mb: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {expectedROI}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expected ROI
                  </Typography>
                </Box>
                
                <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {riskReduction}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Risk Reduction
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Allocation Strategy</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {allocationStrategy}
                </Typography>

                {actionPlan && (
                  <>
                    <Typography variant="h6" gutterBottom>Action Plan</Typography>
                    <List dense>
                      {actionPlan.map((action, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <CheckCircle color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={action} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </MetricCard>
      </Grid>
    );
  };

  const hasAnyInsights = Object.values(insights).some(insight => insight !== null);

  const renderUnifiedDashboard = () => {
    if (!hasAnyInsights) return null;

    return (
      <Grid container spacing={3}>
        {/* Executive Summary Card */}
        <Grid item xs={12}>
          <GradientCard gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2, width: 56, height: 56 }}>
                    <Psychology sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="700">
                      AI Executive Summary
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      Comprehensive Market Intelligence
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  icon={<AutoAwesome />} 
                  label="AI Powered" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '1rem', py: 1 }}
                />
              </Box>

              <Grid container spacing={4}>
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3 }}>
                    <Typography variant="h2" fontWeight="700" sx={{ mb: 1 }}>
                      {insights.marketInsights?.marketSentiment || 'Positive'}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      Market Sentiment
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3 }}>
                    <Typography variant="h2" fontWeight="700" sx={{ mb: 1 }}>
                      {insights.riskAssessment?.portfolioRiskScore || 65}/100
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      Risk Score
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3 }}>
                    <Typography variant="h2" fontWeight="700" sx={{ mb: 1 }}>
                      {insights.portfolioOptimization?.expectedROI || '12.5%'}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      Expected ROI
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3 }}>
                    <Typography variant="h2" fontWeight="700" sx={{ mb: 1 }}>
                      {insights.recommendations?.recommendations?.length || 5}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      AI Recommendations
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {insights.marketInsights?.summary && (
                <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Assessment sx={{ mr: 1 }} />
                    Market Analysis
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                    {insights.marketInsights.summary}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </GradientCard>
        </Grid>

        {/* Investment Opportunities & Risks */}
        <Grid item xs={12} md={6}>
          <MetricCard sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TrendingUp sx={{ mr: 2, color: 'success.main', fontSize: 32 }} />
                <Typography variant="h5" fontWeight="600">
                  Investment Opportunities
                </Typography>
              </Box>
              
              {insights.marketInsights?.topOpportunities ? (
                <List sx={{ flex: 1 }}>
                  {insights.marketInsights.topOpportunities.slice(0, 4).map((opportunity, index) => (
                    <ListItem key={index} sx={{ 
                      px: 0, 
                      py: 2,
                      '&:not(:last-child)': { borderBottom: '1px solid #f0f0f0' }
                    }}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                          <Star sx={{ fontSize: 20 }} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText 
                        primary={opportunity}
                        primaryTypographyProps={{
                          fontWeight: 500,
                          fontSize: '1rem'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Generate insights to see opportunities
                  </Typography>
                </Box>
              )}
            </CardContent>
          </MetricCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <MetricCard sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Warning sx={{ mr: 2, color: 'warning.main', fontSize: 32 }} />
                <Typography variant="h5" fontWeight="600">
                  Risk Factors
                </Typography>
              </Box>
              
              {insights.marketInsights?.risks ? (
                <List sx={{ flex: 1 }}>
                  {insights.marketInsights.risks.slice(0, 4).map((risk, index) => (
                    <ListItem key={index} sx={{ 
                      px: 0, 
                      py: 2,
                      '&:not(:last-child)': { borderBottom: '1px solid #f0f0f0' }
                    }}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                          <Warning sx={{ fontSize: 20 }} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText 
                        primary={risk}
                        primaryTypographyProps={{
                          fontWeight: 500,
                          fontSize: '1rem'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Generate insights to see risk factors
                  </Typography>
                </Box>
              )}
            </CardContent>
          </MetricCard>
        </Grid>

        {/* Market Prediction Chart */}
        {insights.marketPrediction && (
          <Grid item xs={12}>
            <MetricCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <ShowChart sx={{ mr: 2, color: 'info.main', fontSize: 32 }} />
                  <Typography variant="h5" fontWeight="600">
                    Market Trend Prediction
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={generateMockHistoricalData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip />
                    <Area 
                      type="monotone" 
                      dataKey="avgPrice" 
                      stroke="#8884d8" 
                      fill="url(#colorPrice)" 
                      strokeWidth={3}
                    />
                    <defs>
                      <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </MetricCard>
          </Grid>
        )}

        {/* Action Plan */}
        {insights.portfolioOptimization?.actionPlan && (
          <Grid item xs={12}>
            <MetricCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CheckCircle sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                  <Typography variant="h5" fontWeight="600">
                    Recommended Action Plan
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  {insights.portfolioOptimization.actionPlan.map((action, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: 2, 
                        bgcolor: 'primary.50', 
                        borderRadius: 2,
                        borderLeft: '4px solid',
                        borderLeftColor: 'primary.main'
                      }}>
                        <Typography variant="h6" sx={{ mr: 2, color: 'primary.main' }}>
                          {index + 1}
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {action}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </MetricCard>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        p: 3,
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        border: '1px solid rgba(0,0,0,0.08)'
      }}>
        <Box>
          <Typography variant="h4" component="h2" fontWeight="700" gutterBottom color="primary">
            Advanced AI Analytics
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
            AI-powered insights and predictions for {properties.length.toLocaleString()} properties
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <AutoAwesome />}
          onClick={generateAllInsights}
          disabled={loading || !properties.length}
          sx={{ 
            background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: '12px',
            '&:hover': {
              background: 'linear-gradient(45deg, #5a67d8 0%, #6b46c1 100%)',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.6)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          {loading ? 'Generating AI Insights...' : 'Generate AI Insights'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!hasAnyInsights && !loading && (
        <>
          {properties.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No property data available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                AI analytics will be available once property data is loaded.
              </Typography>
            </Box>
          ) : (
            <Paper sx={{ 
              p: 8, 
              textAlign: 'center', 
              borderRadius: '24px',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              border: '1px solid rgba(0,0,0,0.08)'
            }}>
              <Box sx={{ 
                width: 120, 
                height: 120, 
                mx: 'auto', 
                mb: 3,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AutoAwesome sx={{ fontSize: 64, color: 'white' }} />
              </Box>
              <Typography variant="h4" gutterBottom fontWeight="700">
                Unlock AI-Powered Market Intelligence
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 700, mx: 'auto', lineHeight: 1.6 }}>
                Get comprehensive market analysis, risk assessments, and personalized investment recommendations powered by advanced machine learning algorithms.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AutoAwesome />}
                onClick={generateAllInsights}
                size="large"
                sx={{ 
                  background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  borderRadius: '12px',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 30px rgba(102, 126, 234, 0.6)'
                  }
                }}
              >
                Generate Comprehensive Analysis
              </Button>
            </Paper>
          )}
        </>
      )}

      {(hasAnyInsights || loading) && renderUnifiedDashboard()}
    </Box>
  );
};

export default AIInsights; 